import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { Globals } from '@app/shared/globals/globals';
import { TasksMessages } from '../../locale/tasks.messages';
import { CommonMessages } from '@app/constants/common.messages';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { CreateTaskDto } from '@app/domain/task/model/create-task.dto';
import { Task } from '@app/domain/task/model/task.model';
import { PillType } from '@app/shared/components/pill/pill.component';
import { TaskConstants } from '@app/constants/task.constants';
import { TaskAlignmentType } from '../../model/task-alignment-type.enum';
import { UserMinimal } from '@app/models/user/user-minimal.model';
import { IMyDate } from '@nodro7/angular-mydatepicker';
import { DateUtils } from '@app/shared/utils/date.utils';
import { OneToOneScheduleDetailsView } from '@app/domain/one_to_one/model/one-to-one-schedule.model';
import { forkJoin, firstValueFrom } from 'rxjs';
import { OneToOneAPIService } from '@app/domain/one_to_one/api/one-to-one-api.service';
import { Goal } from '@app/models/goals/goal.model';
import { GoalsAPIService } from '@app/shared/api/goals.api.service';
import { CompanyFeatures } from '@app/models/company-features.model';
import { TaskCreatedFrom } from '../../model/task-created-from.model';
import { Role } from '@app/domain/role/model/role.model';
import { RoleBusinessService } from '@app/domain/role/service/role-business.service';
import moment from 'moment';

export interface DefaultTaskAlignment {
  type: TaskAlignmentType;
  id: number;
}

export const initTaskForm = (createdFrom: TaskCreatedFrom, task?: Task, defaultAlignment?: DefaultTaskAlignment, defaultOwners?: UserMinimal[]): UntypedFormGroup => {
  const formGroup = new UntypedFormGroup({
    id: new UntypedFormControl(null, []),
    title: new UntypedFormControl('', [Validators.required, Validators.minLength(TaskConstants.TITLE_MIN_LENGTH), Validators.maxLength(TaskConstants.TITLE_MAX_LENGTH)]),
    description: new UntypedFormControl('', [Validators.maxLength(TaskConstants.DESCRIPTION_MAX_LENGTH)]),
    dueDate: new UntypedFormControl(null, []),
    alignmentType: new UntypedFormControl(TaskAlignmentType.TODO, [Validators.required]),
    alignmentId: new UntypedFormControl(null, []),
    owners: new UntypedFormControl([], []),
    linkedPositionIds: new UntypedFormControl([], []),
    createdFrom: new UntypedFormControl(TaskCreatedFrom.OVERVIEW, [])
  });

  if (defaultAlignment) {
    formGroup.controls.alignmentType.setValue(defaultAlignment.type, { emitEvent: false });
    formGroup.controls.alignmentId.setValue(defaultAlignment.id, { emitEvent: false });
  }

  if (createdFrom && !task) {
    formGroup.controls.createdFrom.setValue(createdFrom, { emitEvent: false });
  }

  if (defaultOwners) {
    formGroup.controls.owners.setValue(defaultOwners, { emitEvent: false });
  }

  if (task) {
    formGroup.controls.id.setValue(task.id, { emitEvent: false });
    formGroup.controls.title.setValue(task.title, { emitEvent: false });
    formGroup.controls.description.setValue(task.description, { emitEvent: false });
    formGroup.controls.dueDate.setValue(task.dueDate, { emitEvent: false });
    formGroup.controls.alignmentType.setValue(task.alignmentType, { emitEvent: false });
    formGroup.controls.alignmentId.setValue(task.alignmentId, { emitEvent: false });
    formGroup.controls.owners.setValue(task.owners, { emitEvent: false });
    formGroup.controls.linkedPositionIds.setValue(task.linkedPositionIds, { emitEvent: false });
    formGroup.controls.createdFrom.setValue(task.createdFrom, { emitEvent: false });
  }

  return formGroup;
};

export const parseTaskFormValueToCreateDto = (taskFormValue: Task): CreateTaskDto => {
  return {
    title: taskFormValue.title,
    description: taskFormValue.description,
    dueDate: taskFormValue.dueDate,
    alignmentType: taskFormValue.alignmentType,
    alignmentId: taskFormValue.alignmentId,
    ownerIds: taskFormValue.owners.map(o => o.id),
    linkedPositionIds: taskFormValue.linkedPositionIds,
    createdFrom: taskFormValue.createdFrom
  };
};

export enum TaskCreatePageViews {
  MAIN = 'MAIN'
}

@Component({
  selector: 'app-task-create',
  templateUrl: './task-create.component.html',
  styleUrls: ['./task-create.component.scss']
})
export class TaskCreateComponent implements OnInit, OnChanges {
  public readonly eTaskCreatePageViews = TaskCreatePageViews;
  public readonly eTaskAlignmentType = TaskAlignmentType;
  public readonly eCompanyFeatures = CompanyFeatures;
  public readonly eCommonMessages = CommonMessages;
  public readonly eTasksMessages = TasksMessages;
  public readonly eTaskConstants = TaskConstants;
  public readonly eButtonType = ButtonType;
  public readonly ePillType = PillType;

  @Input() submitted: boolean;
  @Input() formCreate: UntypedFormGroup;
  @Input() canAlign: boolean;
  @Input() canChangeAlignment: boolean;

  @Output() onFormCancel: EventEmitter<boolean>;
  @Output() onFormSubmit: EventEmitter<boolean>;
  @Output() requestCloseModal: EventEmitter<boolean>;

  public formChanged: boolean;

  selectedView: TaskCreatePageViews;
  ownersCustomOptions: UserMinimal[];
  dueDateDisableUntil: IMyDate;
  oneToOneSchedules: OneToOneScheduleDetailsView[];
  controlTaskAlignedGoal: UntypedFormControl;
  controlTaskAlignedRoles: UntypedFormControl;

  loadingSchedules: boolean;

  get controlTaskId(): UntypedFormControl {
    return this.formCreate.controls.id as UntypedFormControl;
  }

  get controlTaskTitle(): UntypedFormControl {
    return this.formCreate.controls.title as UntypedFormControl;
  }

  get controlTaskDescription(): UntypedFormControl {
    return this.formCreate.controls.description as UntypedFormControl;
  }

  get controlTaskDueDate(): UntypedFormControl {
    return this.formCreate.controls.dueDate as UntypedFormControl;
  }

  get controlTaskAlignmentType(): UntypedFormControl {
    return this.formCreate.controls.alignmentType as UntypedFormControl;
  }

  get controlTaskAlignmentId(): UntypedFormControl {
    return this.formCreate.controls.alignmentId as UntypedFormControl;
  }

  get controlTaskOwners(): UntypedFormControl {
    return this.formCreate.controls.owners as UntypedFormControl;
  }

  get controlLinkedPositionIds(): UntypedFormControl {
    return this.formCreate.controls.linkedPositionIds as UntypedFormControl;
  }

  constructor(
    public globals: Globals,
    private oneToOneAPIService: OneToOneAPIService,
    private goalsAPIService: GoalsAPIService,
    private roleBusinessService: RoleBusinessService
  ) {
    this.formChanged = false;
    this.submitted = false;
    this.canAlign = true;
    this.canChangeAlignment = true;
    this.loadingSchedules = false;

    this.ownersCustomOptions = [];
    this.oneToOneSchedules = [];

    this.selectedView = TaskCreatePageViews.MAIN;

    this.onFormCancel = new EventEmitter<boolean>();
    this.onFormSubmit = new EventEmitter<boolean>();
    this.requestCloseModal = new EventEmitter<boolean>();

    this.dueDateDisableUntil = DateUtils.convertDateMomentToPickerFormat(moment().subtract(1, 'day'));

    this.formCreate = initTaskForm(undefined);
    this.controlTaskAlignedGoal = this.initTaskAlignedGoalForm();
    this.controlTaskAlignedRoles = this.initTaskAlignedRolesForm();
  }

  ngOnInit(): void {
    this.ownersCustomOptions = [ this.globals.user ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changesFormCreate = changes['formCreate'];
    if (changesFormCreate) {
      this.formChanged = false;
      this.initEventListeners();
    }
  }

  initEventListeners(): void {
    this.controlTaskAlignedRoles = this.initTaskAlignedRolesForm();
    this.controlTaskAlignmentType.valueChanges.subscribe(alignmentType => this.onAlignmentTypeChanged(alignmentType));
    this.controlTaskAlignmentId.valueChanges.subscribe(alignmentId => this.onAlignmentIdChanged(alignmentId));

    this.controlTaskAlignmentType.updateValueAndValidity();
    this.controlTaskAlignmentId.updateValueAndValidity();

    this.formCreate.valueChanges.subscribe(() => {
      this.formChanged = true;
    });
  }

  onAlignmentTypeChanged(alignmentType: TaskAlignmentType): void {
    // Reset inner control for goal alignment
    this.controlTaskAlignedGoal = this.initTaskAlignedGoalForm();
    this.controlTaskAlignedRoles = this.initTaskAlignedRolesForm();

    switch(alignmentType) {
      case TaskAlignmentType.ONE_TO_ONE:
        this.getOneToOneAlignmentData();
        break;
      case TaskAlignmentType.TODO :
      case TaskAlignmentType.GOAL:
      default:
        break;
    }
  }

  onAlignmentIdChanged(alignmentId: string): void {
    console.debug('Alignment ID changed', alignmentId);
    this.controlTaskAlignedGoal = this.initTaskAlignedGoalForm();
    this.controlTaskAlignedRoles = this.initTaskAlignedRolesForm();
  }

  getOneToOneAlignmentData(): void {
    this.loadingSchedules = true;

    firstValueFrom(
      forkJoin([
        this.oneToOneAPIService.getOneToOneSchedulesForUserMe(),
        this.oneToOneAPIService.getOneToOneSchedulesForManagerMe()
      ])
    )
      .then(([scheduleEmployee, scheduleManager]) => {
        this.oneToOneSchedules = [...scheduleEmployee, ...scheduleManager];
      })
      .finally(() => {
        this.loadingSchedules = false;
      });
  }

  onViewChangeRequested(view: TaskCreatePageViews): void {
    this.selectedView = view;
  }

  onCancelCreate(): void {
    this.onFormCancel.emit(true);
  }

  onConfirmCreate(): void {
    this.onFormSubmit.emit(true);
  }

  closeCreateModal() {
    this.requestCloseModal.emit(true);
  }

  openHelpArticle() {
    const newWindow = window.open('https://help.frankli.io/en/articles/6723117', '_blank');
    
    if (newWindow) {
      newWindow.focus();
    }
  }

  initTaskAlignedGoalForm(goal?: Goal): UntypedFormControl {
    const formControl = new UntypedFormControl(null, []);

    if (goal) {
      formControl.setValue(goal, { emitEvent: false });
    } else if (this.controlTaskAlignmentType.value === TaskAlignmentType.GOAL && this.controlTaskAlignmentId.value) {
      firstValueFrom(this.goalsAPIService.getGoalById(this.controlTaskAlignmentId.value))
        .then(goalResponse => {
          formControl.setValue(goalResponse, { emitEvent: false });
        });
    }

    formControl.valueChanges.subscribe(goalValue => {
      this.controlTaskAlignmentId.setValue(goalValue ? goalValue.id : null);
    });

    return formControl;
  }

  initTaskAlignedRolesForm(roles?: Role[]): UntypedFormControl {
    const formControl = new UntypedFormControl([], []);

    if (roles) {
      formControl.setValue(roles, { emitEvent: false });
    } else if (this.controlLinkedPositionIds.value && this.controlLinkedPositionIds.value.length > 0) {
      firstValueFrom(this.roleBusinessService.get(this.controlLinkedPositionIds.value, null, null, null))
        .then(res => {
          formControl.setValue(res, { emitEvent: false });
        });
    }

    formControl.valueChanges.subscribe(res => {
      if (res) {
        this.controlLinkedPositionIds.setValue(res.map(r => r.id));
      }
    });

    return formControl;
  }
}
